import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useBlocker } from 'react-router';

import { useTranslations } from '@/i18n/client';

import { UploadProgressContext } from './upload-progress-context';
import { UploadProgressModal } from './UploadProgressModal';
import type { UploadProgressOperation, UploadProgressSnapshot } from './upload-progress-context';

interface UploadProgressProviderProps {
  children: ReactNode;
  navigationBlocking?: boolean;
}

interface ActiveUpload extends UploadProgressSnapshot {
  readonly id: number;
}

interface UploadNavigationBlockerProps {
  progress: UploadProgressSnapshot | null;
  activeUploadRef: React.MutableRefObject<ActiveUpload | null>;
}

function UploadNavigationBlocker({ progress, activeUploadRef }: UploadNavigationBlockerProps) {
  const shouldBlock = useCallback(() => activeUploadRef.current !== null, [activeUploadRef]);
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!progress && blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker, progress]);

  return progress ? <UploadProgressModal progress={progress} blocker={blocker} /> : null;
}

function createOperation(
  id: number,
  activeUploadRef: React.MutableRefObject<ActiveUpload | null>,
  setProgress: React.Dispatch<React.SetStateAction<UploadProgressSnapshot | null>>,
): UploadProgressOperation {
  const update = (callback: (current: ActiveUpload) => ActiveUpload): void => {
    const current = activeUploadRef.current;
    if (!current || current.id !== id) {
      return;
    }

    const next = callback(current);
    activeUploadRef.current = next;
    setProgress(next);
  };

  return {
    setTotalFiles(totalFiles) {
      const normalizedTotal = Math.max(0, Math.floor(totalFiles));
      update((current) => ({
        ...current,
        totalFiles: normalizedTotal,
        uploadedFiles: Math.min(current.uploadedFiles, normalizedTotal),
      }));
    },
    startFile(fileName) {
      update((current) => ({ ...current, currentFileName: fileName, currentFileProgress: 0 }));
    },
    setFileProgress(progress) {
      update((current) => ({
        ...current,
        currentFileProgress: progress === null ? null : Math.max(0, Math.min(100, Math.round(progress))),
      }));
    },
    completeFile() {
      update((current) => ({
        ...current,
        uploadedFiles: Math.min(current.totalFiles, current.uploadedFiles + 1),
        currentFileProgress: 0,
      }));
    },
    finish() {
      const current = activeUploadRef.current;
      if (!current || current.id !== id) {
        return;
      }

      activeUploadRef.current = null;
      setProgress(null);
    },
  };
}

export function UploadProgressProvider({ children, navigationBlocking = true }: UploadProgressProviderProps) {
  const t = useTranslations('common.uploadProgress');
  const [progress, setProgress] = useState<UploadProgressSnapshot | null>(null);
  const activeUploadRef = useRef<ActiveUpload | null>(null);
  const nextIdRef = useRef(0);

  const beginUpload = useCallback((totalFiles: number): UploadProgressOperation | null => {
    const normalizedTotal = Math.max(0, Math.floor(totalFiles));
    if (activeUploadRef.current) {
      return null;
    }

    const id = nextIdRef.current + 1;
    nextIdRef.current = id;
    const next: ActiveUpload = {
      id,
      totalFiles: normalizedTotal,
      uploadedFiles: 0,
      currentFileName: null,
      currentFileProgress: 0,
    };
    activeUploadRef.current = next;
    setProgress(next);

    return createOperation(id, activeUploadRef, setProgress);
  }, []);

  useEffect(() => {
    if (!progress) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = t('beforeUnloadWarning');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [progress, t]);

  const contextValue = { progress, beginUpload };

  return (
    <UploadProgressContext.Provider value={contextValue}>
      {children}
      {navigationBlocking ? (
        <UploadNavigationBlocker progress={progress} activeUploadRef={activeUploadRef} />
      ) : progress ? (
        <UploadProgressModal progress={progress} />
      ) : null}
    </UploadProgressContext.Provider>
  );
}
